/**
 * @file useComicSearchPage.js
 * @description 漫画搜索/分类页：关键词搜索与分类筛选分流、分页与移动端滚动加载
 * @module features/comic/composables/useComicSearchPage
 */

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import { convertRouteParams } from '@/shared/utils/idHelper'
import {
  CHARGE_TYPE_FILTER_OPTIONS,
  REGION_FILTER_OPTIONS,
  SERIAL_STATUS_FILTER_OPTIONS,
  SORT_TYPE_FILTER_OPTIONS,
} from '@/features/comic/constants/comicClassifyFilters'
import {
  classifyComicApi,
  fetchComicThemeSonSelectApi,
  searchComicApi,
} from '@/features/comic/api/comicApi'
import { normalizeComicListItems } from '@/features/comic/utils/normalizeComicListItem'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'

/** @typedef {'search' | 'classify'} ComicSearchMode */

/**
 * @param {import('vue-router').LocationQuery} query
 * @returns {ComicSearchMode}
 */
function resolveModeFromQuery(query) {
  if (query.mode === 'search' || query.mode === 'classify') {
    return query.mode
  }
  if (query.type === 'KEYWORD' || (query.keyword && String(query.keyword).trim())) {
    return 'search'
  }
  return 'classify'
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function queryStringValue(value) {
  if (Array.isArray(value)) return String(value[0] ?? '')
  return value != null ? String(value) : ''
}

/**
 * @param {(event: 'show-popup-message', message: string, type: string) => void} emit
 */
export function useComicSearchPage(emit) {
  const route = useRoute()
  const router = useRouter()

  const comics = ref([])
  const categories = ref([])
  const currentPage = ref(1)
  const totalPages = ref(0)
  const totalCount = ref(0)
  const currentPageSize = ref(10)
  const loading = ref(true)
  const mode = ref(/** @type {ComicSearchMode} */ ('classify'))
  const keyword = ref('')
  const selectedThemeCode = ref('')
  const bigThemeCode = ref(null)
  const regionCode = ref('')
  const serialStatus = ref('')
  const chargeType = ref('')
  const sortType = ref('POPULARITY')
  const isMobile = ref(false)
  const isLoadingMore = ref(false)
  let scrollDebounceTimer = null

  const hasMore = computed(() => currentPage.value < totalPages.value)
  const isClassifyMode = computed(() => mode.value === 'classify')
  const isSearchMode = computed(() => mode.value === 'search')

  const themeFilterOptions = computed(() => {
    const themeOptions = categories.value.map((category) => ({
      value: String(category.themeCode),
      label: category.themeName,
    }))
    return [{ value: '', label: '全部' }, ...themeOptions]
  })

  function updateIsMobile() {
    isMobile.value = isMobileLayoutViewport()
  }

  function getPageSize() {
    return isMobile.value ? 6 : 12
  }

  /**
   * @param {number|string} total
   * @param {number|string} [pages]
   */
  function applyPagination(total, pages) {
    const parsedTotal = Number(total) || 0
    totalCount.value = parsedTotal
    if (parsedTotal > 0 && currentPageSize.value > 0) {
      totalPages.value = Math.ceil(parsedTotal / currentPageSize.value)
      return
    }
    const parsedPages = Number(pages)
    totalPages.value = parsedPages > 0 ? parsedPages : 1
  }

  function buildClassifyRequestData(pageNum) {
    /** @type {import('@/features/comic/constants/comicClassifyFilters').ClassifyComicPayload} */
    const requestData = {
      pageNum,
      pageSize: currentPageSize.value,
      sortType: sortType.value || 'POPULARITY',
    }

    if (bigThemeCode.value != null) {
      requestData.bigThemeCode = bigThemeCode.value
    }
    if (selectedThemeCode.value) {
      requestData.themeArray = [selectedThemeCode.value]
    }
    if (regionCode.value) requestData.regionCode = regionCode.value
    if (serialStatus.value) requestData.serialStatus = serialStatus.value
    if (chargeType.value) requestData.chargeType = chargeType.value

    return requestData
  }

  function buildSearchRequestData(pageNum) {
    return {
      keyword: keyword.value.trim(),
      pageNum,
      pageSize: currentPageSize.value,
    }
  }

  /**
   * @param {import('vue-router').LocationQuery} query
   */
  function applyRouteQuery(query) {
    mode.value = resolveModeFromQuery(query)
    keyword.value = queryStringValue(query.keyword)

    const themeQuery = queryStringValue(query.themeArray)
    selectedThemeCode.value = themeQuery ? themeQuery.split(',')[0].trim() : ''

    const parsedBigTheme = parseInt(queryStringValue(query.bigThemeCode), 10)
    bigThemeCode.value = Number.isNaN(parsedBigTheme) ? null : parsedBigTheme
    if (themeQuery) {
      bigThemeCode.value = null
    }

    regionCode.value = queryStringValue(query.regionCode)
    serialStatus.value = queryStringValue(query.serialStatus)
    chargeType.value = queryStringValue(query.chargeType)
    sortType.value = queryStringValue(query.sortType) || 'POPULARITY'
  }

  /**
   * 将当前筛选状态同步到 URL（replace 避免堆叠历史）
   */
  function syncRouteQuery() {
    if (mode.value === 'search') {
      router.replace({
        name: 'ComicSearch',
        query: {
          mode: 'search',
          keyword: keyword.value.trim(),
        },
      })
      return
    }

    /** @type {Record<string, string>} */
    const query = { mode: 'classify' }

    if (bigThemeCode.value != null) {
      query.bigThemeCode = String(bigThemeCode.value)
    }
    if (selectedThemeCode.value) {
      query.themeArray = selectedThemeCode.value
    }
    if (regionCode.value) query.regionCode = regionCode.value
    if (serialStatus.value) query.serialStatus = serialStatus.value
    if (chargeType.value) query.chargeType = chargeType.value
    if (sortType.value && sortType.value !== 'POPULARITY') {
      query.sortType = sortType.value
    }

    router.replace({ name: 'ComicSearch', query })
  }

  function showPopupMessage(message, popupType) {
    emit('show-popup-message', message, popupType)
  }

  async function fetchCategories() {
    try {
      const response = await fetchComicThemeSonSelectApi()
      if (response.data.code === 200 || response.data.code === 0) {
        categories.value = response.data.data.comicThemeArray || []
      } else {
        showPopupMessage(response.data.msg || '获取分类失败', 'error')
      }
    } catch (error) {
      console.error('获取分类异常:', error)
      showPopupMessage(error.message || '分类加载失败', 'error')
    }
  }

  function checkAndLoadMoreIfNeeded() {
    if (!isMobile.value || isLoadingMore.value || !hasMore.value) return

    const scrollTop =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight
    const documentHeight =
      document.documentElement.scrollHeight || document.body.scrollHeight

    if (documentHeight <= windowHeight + 50) {
      loadMore()
    }
  }

  /**
   * @param {number} pageNum
   */
  async function requestComicPage(pageNum) {
    if (mode.value === 'search') {
      if (!keyword.value.trim()) {
        throw new Error('搜索关键词不能为空')
      }
      return searchComicApi(buildSearchRequestData(pageNum))
    }
    return classifyComicApi(buildClassifyRequestData(pageNum))
  }

  async function fetchComicsData() {
    try {
      // 移动端筛选刷新时保留旧列表，底部 loading 提示，避免整页空白
      if (currentPage.value === 1 && !isMobile.value) {
        comics.value = []
      }

      const response = await requestComicPage(currentPage.value)
      const data = response.data

      if (data.code === 200 || data.code === 0) {
        comics.value = normalizeComicListItems(data.contents)
        applyPagination(data.total ?? data.totalCount ?? 0, data.pages)

        if (isMobile.value) {
          nextTick(() => {
            checkAndLoadMoreIfNeeded()
          })
        }
      } else {
        showPopupMessage(data.msg || '获取数据失败', 'error')
      }
    } catch (error) {
      console.error('Error fetching comics: ', error)
      showPopupMessage(error.message || '请求失败', 'error')
    } finally {
      loading.value = false
    }
  }

  async function loadMore() {
    if (isLoadingMore.value || !hasMore.value) return

    isLoadingMore.value = true
    const nextPage = currentPage.value + 1

    try {
      const response = await requestComicPage(nextPage)
      const data = response.data

      if (data.code === 200 || data.code === 0) {
        const newContents = normalizeComicListItems(data.contents)
        comics.value = [...comics.value, ...newContents]
        currentPage.value = nextPage
        applyPagination(data.total ?? data.totalCount ?? 0, data.pages)

        nextTick(() => {
          checkAndLoadMoreIfNeeded()
        })
      } else {
        showPopupMessage(data.msg || '加载更多失败', 'error')
      }
    } catch (error) {
      console.error('Error loading more comics: ', error)
      showPopupMessage(error.message || '加载更多失败', 'error')
    } finally {
      isLoadingMore.value = false
    }
  }

  function handleScroll() {
    if (!isMobile.value) return
    if (isLoadingMore.value || !hasMore.value) return

    if (scrollDebounceTimer) {
      clearTimeout(scrollDebounceTimer)
    }

    scrollDebounceTimer = setTimeout(() => {
      const scrollTop =
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop
      const windowHeight =
        window.innerHeight || document.documentElement.clientHeight
      const documentHeight =
        document.documentElement.scrollHeight || document.body.scrollHeight
      const distanceToBottom = documentHeight - (scrollTop + windowHeight)

      if (distanceToBottom <= 200) {
        loadMore()
      }
    }, 200)
  }

  function handleResize() {
    const wasMobile = isMobile.value
    updateIsMobile()
    if (wasMobile !== isMobile.value) {
      currentPageSize.value = getPageSize()
      currentPage.value = 1
      if (isMobile.value) {
        window.addEventListener('scroll', handleScroll, { passive: true })
      } else {
        window.removeEventListener('scroll', handleScroll)
      }
      fetchComicsData()
    }
  }

  function resetListAndFetch() {
    currentPage.value = 1
    if (!isMobile.value) {
      comics.value = []
    }
    isLoadingMore.value = false
    loading.value = true
    syncRouteQuery()
    fetchComicsData()
  }

  function changeTheme(themeCode) {
    selectedThemeCode.value = themeCode
    bigThemeCode.value = null
    resetListAndFetch()
  }

  function changeRegion(value) {
    regionCode.value = value
    resetListAndFetch()
  }

  function changeSerialStatus(value) {
    serialStatus.value = value
    resetListAndFetch()
  }

  function changeChargeType(value) {
    chargeType.value = value
    resetListAndFetch()
  }

  function changeSortType(value) {
    sortType.value = value || 'POPULARITY'
    resetListAndFetch()
  }

  function handlePageChange(page) {
    if (page !== currentPage.value) {
      currentPage.value = page
      loading.value = true
      fetchComicsData()
    }
  }

  function handlePageSizeChange(newPageSize) {
    if (newPageSize !== currentPageSize.value) {
      currentPageSize.value = newPageSize
      currentPage.value = 1
      loading.value = true
      fetchComicsData()
    }
  }

  function goToComicDetail(id) {
    router.push({
      name: 'ComicDetail',
      params: convertRouteParams({ id }),
    })
  }

  /**
   * @param {import('vue-router').LocationQuery} query
   */
  function resetForRouteUpdate(query) {
    applyRouteQuery(query)
    currentPage.value = 1
    if (!isMobile.value) {
      comics.value = []
    }
    isLoadingMore.value = false
    loading.value = true
    fetchComicsData()
  }

  onBeforeRouteUpdate((to, _from, next) => {
    resetForRouteUpdate(to.query)
    next()
  })

  onMounted(() => {
    updateIsMobile()
    currentPageSize.value = getPageSize()
    applyRouteQuery(route.query)
    fetchCategories()
    fetchComicsData()

    window.addEventListener('resize', handleResize)
    nextTick(() => {
      if (isMobile.value) {
        window.addEventListener('scroll', handleScroll, { passive: true })
      }
    })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize)
    window.removeEventListener('scroll', handleScroll)
    if (scrollDebounceTimer) {
      clearTimeout(scrollDebounceTimer)
    }
  })

  return {
    comics,
    categories,
    currentPage,
    totalPages,
    totalCount,
    currentPageSize,
    loading,
    mode,
    keyword,
    selectedThemeCode,
    isMobile,
    isLoadingMore,
    hasMore,
    isClassifyMode,
    isSearchMode,
    themeFilterOptions,
    regionFilterOptions: REGION_FILTER_OPTIONS,
    serialStatusFilterOptions: SERIAL_STATUS_FILTER_OPTIONS,
    chargeTypeFilterOptions: CHARGE_TYPE_FILTER_OPTIONS,
    sortTypeFilterOptions: SORT_TYPE_FILTER_OPTIONS,
    regionCode,
    serialStatus,
    chargeType,
    sortType,
    changeTheme,
    changeRegion,
    changeSerialStatus,
    changeChargeType,
    changeSortType,
    handlePageChange,
    handlePageSizeChange,
    goToComicDetail,
  }
}
